/**
 * Keeps CSS variables in sync with the *visual viewport* (mobile browser chrome + keyboard aware).
 *
 * Why this exists:
 * - Android/Samsung browsers may oscillate `visualViewport.height` while the keyboard animates.
 *   Updating a fixed dialog on every tick can cause visible jitter.
 * - We therefore freeze `--vvh` while an editable element inside `container` is focused,
 *   then commit the final height after a short settle delay.
 *
 * Sets on :root (documentElement):
 * - `--vvh`: visual viewport height in px (may be frozen during keyboard animation)
 * - `--vvt`: visual viewport offsetTop in px (always updated)
 */
import { useEffect, useCallback, useRef } from 'react';
var isEditableElement = function (element) {
    return element.tagName === 'INPUT' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'SELECT' ||
        !!element.isContentEditable;
};
// Find an editable element from a focus event even when it originates in shadow DOM.
var findEditableFromFocusEvent = function (event) {
    // IMPORTANT: don't extract `composedPath` into a variable and call it later.
    // On some browsers this results in "Illegal invocation" because the method loses its binding.
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var _i = 0, path_1 = path; _i < path_1.length; _i++) {
        var node = path_1[_i];
        if (node instanceof HTMLElement && isEditableElement(node))
            return node;
    }
    var target = event.target;
    if (target instanceof HTMLElement && isEditableElement(target))
        return target;
    return null;
};
var isElementInContainer = function (element, container) {
    if (!container)
        return true;
    return container.contains(element);
};
export function useVisualViewportHeight(container) {
    // Freeze height updates while the keyboard is animating to avoid Samsung jitter.
    var frozenRef = useRef(false);
    // Whether an editable element inside the container is currently focused.
    // Using a ref avoids querying `document.activeElement` on every resize tick.
    var focusedEditableRef = useRef(false);
    var focusTimerRef = useRef(null);
    // Coalesce resize/scroll bursts into a single DOM write per frame.
    var rafIdRef = useRef(null);
    var clearFocusTimer = useCallback(function () {
        if (focusTimerRef.current) {
            clearTimeout(focusTimerRef.current);
            focusTimerRef.current = null;
        }
    }, []);
    var cancelRaf = useCallback(function () {
        if (rafIdRef.current != null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
    }, []);
    var setViewportVariables = useCallback(function (forceUpdate) {
        var _a, _b;
        if (forceUpdate === void 0) { forceUpdate = false; }
        var vv = window.visualViewport;
        var height = (_a = vv === null || vv === void 0 ? void 0 : vv.height) !== null && _a !== void 0 ? _a : window.innerHeight;
        var offsetTop = (_b = vv === null || vv === void 0 ? void 0 : vv.offsetTop) !== null && _b !== void 0 ? _b : 0;
        var rootStyle = document.documentElement.style;
        // Always update offsetTop for correct positioning under address bar / safe-area.
        rootStyle.setProperty('--vvt', "".concat(offsetTop, "px"));
        // Skip height updates while typing/keyboard animation is in progress.
        if (!forceUpdate && (frozenRef.current || focusedEditableRef.current))
            return;
        rootStyle.setProperty('--vvh', "".concat(height, "px"));
    }, []);
    var scheduleViewportUpdate = useCallback(function (forceUpdate) {
        if (forceUpdate === void 0) { forceUpdate = false; }
        // If forceUpdate is requested, flush synchronously (used for init/focusout/orientation).
        if (forceUpdate) {
            cancelRaf();
            setViewportVariables(true);
            return;
        }
        if (rafIdRef.current != null)
            return;
        rafIdRef.current = requestAnimationFrame(function () {
            rafIdRef.current = null;
            setViewportVariables(false);
        });
    }, [cancelRaf, setViewportVariables]);
    var handleViewportChange = useCallback(function () {
        scheduleViewportUpdate(false);
    }, [scheduleViewportUpdate]);
    var handleOrientationChange = useCallback(function () {
        // Orientation changes are a hard reset: stop freezing and take a fresh measurement.
        frozenRef.current = false;
        focusedEditableRef.current = false;
        clearFocusTimer();
        scheduleViewportUpdate(true);
    }, [scheduleViewportUpdate, clearFocusTimer]);
    var handleFocusIn = useCallback(function (event) {
        var editable = findEditableFromFocusEvent(event);
        if (!editable)
            return;
        if (!isElementInContainer(editable, container))
            return;
        focusedEditableRef.current = true;
        frozenRef.current = true;
        clearFocusTimer();
        // Let the keyboard animation settle, then commit the final height once.
        focusTimerRef.current = setTimeout(function () {
            scheduleViewportUpdate(true);
        }, 250);
    }, [container, scheduleViewportUpdate, clearFocusTimer]);
    var handleFocusOut = useCallback(function () {
        focusedEditableRef.current = false;
        frozenRef.current = false;
        clearFocusTimer();
        scheduleViewportUpdate(true);
    }, [scheduleViewportUpdate, clearFocusTimer]);
    useEffect(function () {
        var vv = window.visualViewport;
        // Init immediately.
        scheduleViewportUpdate(true);
        // Viewport changes (keyboard / browser chrome / pinch-zoom)
        vv === null || vv === void 0 ? void 0 : vv.addEventListener('resize', handleViewportChange);
        vv === null || vv === void 0 ? void 0 : vv.addEventListener('scroll', handleViewportChange);
        // Fallback / desktop
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('orientationchange', handleOrientationChange);
        // Use focus cues to decide when to freeze height (no browser sniffing).
        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);
        return function () {
            clearFocusTimer();
            cancelRaf();
            vv === null || vv === void 0 ? void 0 : vv.removeEventListener('resize', handleViewportChange);
            vv === null || vv === void 0 ? void 0 : vv.removeEventListener('scroll', handleViewportChange);
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);
        };
    }, [
        container,
        handleViewportChange,
        handleOrientationChange,
        handleFocusIn,
        handleFocusOut,
        scheduleViewportUpdate,
        clearFocusTimer,
        cancelRaf,
    ]);
}
